import * as R from "ramda";

import {
  showMediaOssUrl,
  uploadMediaOssUrl,
  previewMediaOssBaseUrlWithCdn,
} from "./upload-resource";
import { Image } from "@/util/ApiList";

/**
 * @param size
 * @param limit 22K, 10M, 1G...
 */
export const isLessThanHumanReadableSize = (size, limit) => {
  const realSizeOfLimit = (() => {
    const lowerLimit = limit.toLowerCase();
    if (lowerLimit.includes("k")) {
      return parseFloat(limit) * 1024;
    }
    if (lowerLimit.includes("m")) {
      return parseFloat(limit) * 1024 * 1024;
    }
    if (lowerLimit.includes("g")) {
      return parseFloat(limit) * 1024 * 1024 * 1024;
    }
    return parseFloat(limit);
  })();

  return size <= realSizeOfLimit;
};

export const sizeToHumanReadableSize = (size) => {
  const gb = 1024 * 1024 * 1024;
  const mb = 1024 * 1024;
  const kb = 1024;
  if (size >= gb) {
    return `${(size / gb).toFixed(2)}G`;
  }
  if (size >= mb) {
    return `${(size / mb).toFixed(2)}M`;
  }
  if (size >= kb) {
    return `${(size / kb).toFixed(2)}K`;
  }

  return `${size}B`;
};

export const composeUploadParam = (sign) => {
  return {
    policy: R.pathOr("", ["policy"], sign),
    OSSAccessKeyId: R.pathOr("", ["accessid"], sign),
    success_action_status: "200",
    callback: R.pathOr(R.T, ["callback"], sign),
    signature: R.pathOr("", ["signature"], sign),
  };
};

export const replaceNameWithRandomName = (filename) => {
  return `${Math.random().toString(36).substring(2)}${filename.substring(
    filename.lastIndexOf(".")
  )}`;
};

export const getKey = (orderId, galleryId, userId, file) => {
  return `${orderId}/${userId}/${replaceNameWithRandomName(file.name)}`;
};

export const getMp4KeyByUrl = () => {
  /**
   * orderId 的获取方法
   * 1. pathName
   * 2. split by '/'
   * 3. reverse
   * 4. array[1]
   * @type {string}
   */

  const orderId = R.pipe(
    R.pathOr("", ["location", "pathname"]),
    R.split("/"),
    R.reverse,
    R.nth(1),
    R.defaultTo("0")
  )(window);
  const userId = sessionStorage.getItem("userId");
  return `${orderId}/${userId}/${Math.random().toString(36).substring(2)}.mp4`;
};

export const getRandomFullPathOfMp4 = () => {
  return `https:${uploadMediaOssUrl}${getMp4KeyByUrl()}`;
};

export const getNewSign = () =>
  new Promise((resolve, reject) => {
    fetch(Image.sign.url, {
      method: Image.sign.method,
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        resolve(composeUploadParam(data));
      })
      .catch((error) => {
        reject(error);
      });
  });

export const uploadFileToOss = ({
  key,
  file,
  onProgress = R.T,
  abortRef,
  action = previewMediaOssBaseUrlWithCdn,
}) => {
  return new Promise((resolve, reject) => {
    getNewSign()
      .then((data) => {
        const controller = new window.AbortController();
        const signal = controller.signal;

        const formData = new FormData();
        formData.append("key", key.startsWith("/") ? key.substring(1) : key);
        Object.keys({ ...data }).forEach((key) => {
          formData.append(key, data[key]);
        });
        formData.append("file", file);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", action, true);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded / event.total);
          }
        };
        // 定义当请求被取消时的处理逻辑
        signal.addEventListener("abort", () => {
          xhr.abort(); // 调用 XMLHttpRequest 的 abort 方法
        });

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(xhr.responseText);
          } else {
            reject(new Error("Upload failed"));
          }
        };
        xhr.onerror = () => {
          reject(new Error("Upload failed"));
        };
        xhr.send(formData);

        if (abortRef) {
          abortRef.current = () => {
            controller.abort();
          };
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const uploadFile = (key, file, onProgress = R.T, abortRef) => {
  return uploadFileToOss({
    key,
    file,
    onProgress,
    abortRef,
    action: showMediaOssUrl,
  });
};

export const getMediaTypeByFile = (file) => {
  const type = file.type;
  if (type.includes("video")) {
    return "video";
  } else if (type.includes("audio")) {
    return "audio";
  } else if (type.includes("image")) {
    return "image";
  } else if (
    type.includes("text") ||
    file.name.toLowerCase().endsWith(".ass")
  ) {
    return "text";
  }
};

export const getMd5OfFile = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    debugger;
    console.log(e);
  }
};

function createAMemoryFileByStringContent(content) {
  if (typeof content === "object") {
    // If the content is a JSON object, convert it to a string first
    content = JSON.stringify(content, null, 2); // Format JSON for readability
  }

  const blob = new Blob([content], { type: "application/json" }); // Create a Blob file
  return new File([blob], "file.json", { type: "application/json" }); // Create a File object
}

export const uploadContentToOssFile = (url, content) => {
  return new Promise((resolve, reject) => {
    getNewSign()
      .then((data) => {
        const controller = new window.AbortController();
        const signal = controller.signal;
        const key = url.startsWith("/") ? url.substring(1) : url;
        const formData = new FormData();
        formData.append("key", key);
        Object.keys({ ...data }).forEach((key) => {
          formData.append(key, data[key]);
        });
        formData.append("file", createAMemoryFileByStringContent(content));
        const xhr = new XMLHttpRequest();
        xhr.open("POST", uploadMediaOssUrl, true);
        // 定义当请求被取消时的处理逻辑
        signal.addEventListener("abort", () => {
          xhr.abort(); // 调用 XMLHttpRequest 的 abort 方法
        });

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(xhr.responseText);
          } else {
            reject(new Error("Upload failed"));
          }
        };
        xhr.onerror = () => {
          reject(new Error("Upload failed"));
        };
        xhr.send(formData);
      })
      .catch((error) => {
        reject(error);
      });
  });
};


// WEBPACK FOOTER //
// ./src/containers/Video/widgets/upload-resource-util.js